import { z } from "zod";
// ─── QuickBooks ────────────────────────────────────────────
export const QboConnectionStateSchema = z.enum([
    "not_configured",
    "ready_to_connect",
    "connected",
    "needs_reconnect",
    "degraded",
]);
export const QboConnectionStatusSchema = z.object({
    configured: z.boolean(),
    connected: z.boolean(),
    state: QboConnectionStateSchema,
    realmId: z.string().nullable(),
    environment: z.enum(["sandbox", "production"]),
    tokenExpiresAt: z.string().datetime().nullable(),
    message: z.string(),
});
export const QboAdminConfigSaveSchema = z.object({
    clientId: z.string().min(1).max(200),
    redirectUri: z.string().url().max(500),
    environment: z.enum(["sandbox", "production"]),
    clientSecret: z.string().min(1).max(500).optional(),
});
export const QboAdminConfigSchema = z.object({
    configured: z.boolean(),
    connected: z.boolean(),
    config: z.object({
        clientId: z.string().max(200).optional(),
        redirectUri: z.string().max(500).optional(),
        environment: z.enum(["sandbox", "production"]).optional(),
    }),
    hasClientSecret: z.boolean(),
    realmId: z.string().nullable(),
    updatedBy: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const CashflowQboDataStateSchema = z.enum([
    "manual_only",
    "ready_to_connect",
    "live",
    "degraded",
]);
// ─── Cashflow Item ─────────────────────────────────────────
export const CashflowApprovalStatusSchema = z.enum(["pending", "approved", "rejected"]);
export const CashflowItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    type: z.enum(["income", "expense"]),
    amount: z.number().positive(),
    currency: z.string().length(3).default("GBP"),
    date: z.string().date(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    recurrence: z.enum(["once", "monthly", "quarterly", "annual"]),
    endDate: z.string().date().nullable().default(null),
    category: z.string().max(100).optional(),
    notes: z.string().max(1000).optional(),
    source: z.enum(["manual", "qbo_recurring", "qbo_ar", "pipeline", "receipt"]).default("manual"),
    approvalStatus: CashflowApprovalStatusSchema.default("approved"),
    approvalRequestedBy: z.string().max(320).nullable().default(null),
    approvedBy: z.string().max(320).nullable().default(null),
    approvedAt: z.string().datetime().nullable().default(null),
    rejectedBy: z.string().max(320).nullable().default(null),
    rejectedAt: z.string().datetime().nullable().default(null),
    approvalNotes: z.string().max(1000).nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CashflowItemCreateSchema = z.object({
    name: z.string().min(1).max(200),
    type: z.enum(["income", "expense"]),
    amount: z.number().positive(),
    currency: z.string().length(3).default("GBP"),
    date: z.string().date(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    recurrence: z.enum(["once", "monthly", "quarterly", "annual"]),
    endDate: z.string().date().nullable().optional(),
    category: z.string().max(100).optional(),
    notes: z.string().max(1000).optional(),
});
export const CashflowItemUpdateSchema = CashflowItemCreateSchema.partial();
export const CashflowItemApprovalSchema = z.object({
    note: z.string().max(1000).optional(),
});
export const CashflowItemRejectionSchema = z.object({
    reason: z.string().min(1).max(1000),
});
export const CashflowItemAuditEntrySchema = z.object({
    id: z.string().uuid(),
    resourceId: z.string(),
    action: z.string(),
    userId: z.string(),
    details: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.string().datetime(),
});
export const CashflowItemAuditListResponseSchema = z.object({
    audit: z.array(CashflowItemAuditEntrySchema),
});
// ─── Cashflow Settings ─────────────────────────────────────
export const CashflowSettingsSchema = z.object({
    includePipeline: z.boolean().default(false),
    excludedItemIds: z.array(z.string().uuid()).default([]),
    useManualOpeningBalance: z.boolean().default(false),
    manualOpeningBalance: z.number().nullable().default(null),
    bankBalanceOverrides: z.record(z.string(), z.number()).default({}),
});
// ─── Forecast Response ─────────────────────────────────────
export const CashflowForecastItemSchema = z.object({
    id: z.string(),
    source: z.string(),
    name: z.string(),
    type: z.enum(["income", "expense"]),
    amount: z.number(),
    currency: z.string(),
    gbpAmount: z.number(),
    date: z.string(),
    month: z.string(),
    category: z.string().optional(),
    approvalStatus: CashflowApprovalStatusSchema.default("approved"),
    excluded: z.boolean(),
});
export const CashflowMonthSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    label: z.string(),
    income: z.number(),
    expenses: z.number(),
    netFlow: z.number(),
    closingBalance: z.number(),
    items: z.array(CashflowForecastItemSchema),
});
export const CashflowVarianceDriverSchema = z.object({
    id: z.string(),
    source: z.string(),
    name: z.string(),
    type: z.enum(["income", "expense"]),
    category: z.string().nullable(),
    previousAmount: z.number(),
    currentAmount: z.number(),
    variance: z.number(),
    netFlowImpact: z.number(),
});
export const CashflowVarianceMonthSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    label: z.string(),
    previousMonth: z
        .string()
        .regex(/^\d{4}-\d{2}$/)
        .nullable(),
    previousLabel: z.string().nullable(),
    incomeVariance: z.number(),
    expensesVariance: z.number(),
    netFlowVariance: z.number(),
    closingBalanceVariance: z.number(),
    status: z.enum(["baseline", "improved", "worsened", "stable", "risk"]),
    explanation: z.string(),
    topDrivers: z.array(CashflowVarianceDriverSchema),
    topIncomeDrivers: z.array(CashflowVarianceDriverSchema),
    topExpenseDrivers: z.array(CashflowVarianceDriverSchema),
});
export const BankAccountSchema = z.object({
    id: z.string(),
    name: z.string(),
    balance: z.number(),
    currency: z.string(),
});
export const CashflowForecastResponseSchema = z.object({
    openingBalance: z.number(),
    openingBalanceSource: z.enum(["qbo", "manual"]),
    months: z.array(CashflowMonthSchema),
    varianceAnalysis: z.array(CashflowVarianceMonthSchema).default([]),
    bankAccounts: z.array(BankAccountSchema),
    fxRates: z.record(z.string(), z.number()),
    qboConnected: z.boolean(),
    qboStatus: QboConnectionStatusSchema,
    qboDataState: CashflowQboDataStateSchema,
    qboWarning: z.string().nullable(),
    includePipeline: z.boolean().optional(),
    forecastHorizon: z.number().int(),
    itemCount: z.number().int(),
});
// ─── Query ─────────────────────────────────────────────────
export const CashflowQuerySchema = z.object({
    months: z.coerce.number().int().min(1).max(24).default(6),
});
//# sourceMappingURL=cashflow.js.map